/**
 * EmailListInput — the one multi-address field used wherever the app collects a list
 * of email recipients (Reports subscribe, Settings → Training, Settings → Alerts).
 * Each address becomes a removable chip; Enter / comma / semicolon / space / blur
 * commit the draft, and a pasted list is split into one chip per address. Addresses
 * are validated, lower-cased and de-duplicated here so every caller stores the same
 * shape (the server still validates).
 */
import { useId, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Mail, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SPLIT_RE = /[\s,;]+/;

interface EmailListInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  id?: string;
  max?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function EmailListInput({
  value,
  onChange,
  id,
  max,
  placeholder = 'Add an email address',
  disabled,
  className,
}: EmailListInputProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const errorId = useId();

  /** Add every valid token; returns the ones that were rejected. */
  const addTokens = (tokens: string[]): string[] => {
    const next = [...value];
    const rejected: string[] = [];
    let overMax = false;
    for (const raw of tokens) {
      const email = raw.trim().toLowerCase();
      if (!email) continue;
      if (!EMAIL_RE.test(email)) { rejected.push(raw.trim()); continue; }
      if (next.includes(email)) continue;
      if (max != null && next.length >= max) { overMax = true; continue; }
      next.push(email);
    }
    if (next.length !== value.length) onChange(next);
    if (overMax) setError(`You can add up to ${max} address${max === 1 ? '' : 'es'}.`);
    else if (rejected.length) setError(`Not a valid email address: ${rejected.join(', ')}`);
    else setError(null);
    return rejected;
  };

  const commitDraft = () => {
    if (!draft.trim()) return;
    const before = value.length;
    const rejected = addTokens(draft.split(SPLIT_RE));
    // Keep what could not be added so it can be corrected, not silently lost.
    if (rejected.length) { setDraft(rejected.join(' ')); return; }
    if (max != null && before >= max) return;
    setDraft('');
  };

  const remove = (next: string[]) => {
    setError(null); // a stale "not valid" message must not outlive the edit
    onChange(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // IME: Enter/space confirm the composition
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && !draft && value.length) {
      remove(value.slice(0, -1));
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!SPLIT_RE.test(text.trim())) return; // a single address pastes normally
    e.preventDefault();
    addTokens(text.split(SPLIT_RE));
    setDraft('');
  };

  return (
    <div className={className}>
      <div
        className={cn(
          'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 text-sm',
          'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {value.map((email) => (
          <span key={email} className="flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-foreground">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            {email}
            <button
              type="button"
              disabled={disabled}
              onClick={() => remove(value.filter((x) => x !== email))}
              className="text-muted-foreground hover:text-foreground"
              aria-label={`Remove ${email}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="text"
          inputMode="email"
          autoComplete="off"
          value={draft}
          disabled={disabled}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(null); }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={commitDraft}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="min-w-[12rem] flex-1 bg-transparent py-1 text-foreground outline-none placeholder:text-muted-foreground"
        />
      </div>
      {error && <p id={errorId} role="alert" className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
